import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { showNotification } from "@mantine/notifications";
import { File as _File } from "src/graphql/generated/graphql";
import { uploadImageMutation } from "src/graphql/operations/files/mutations";
import { graphqlFetch } from "src/graphql/utils/graphqlFetch";
import { GraphQLRequestError } from "src/graphql/utils/utils";
import { handleError, Translation } from "src/utils/error";
import { getAsByteArray } from "src/utils/getAsByteArray";
import { IMAGE_FORMATS_LABEL } from "src/utils/imageUpload";

export const useUploadImage = () => {
    const { t } = useTranslation(["common"]);

    const [isUploadingImage, setIsUploadingImage] = useState(false);

    const uploadImage = useCallback(
        async (image: File): Promise<_File> => {
            setIsUploadingImage(true);

            try {
                const response = await graphqlFetch(uploadImageMutation, {
                    filename: image.name,
                });

                const presignedUploadUrl = response.uploadImage.presignedUploadUrl;

                const fileByteArray = await getAsByteArray(image);

                const uploadResponse = await fetch(presignedUploadUrl, {
                    method: "PUT",
                    body: fileByteArray,
                });

                // fetch resolves on a 403 from S3; without this the caller got an image whose bytes
                // were never stored. The uploadImage Lambda writes the `:FILE:` row before the upload,
                // and getImages checks only that row, so the inquiry used to save and point at an
                // image that was never stored. The URL is left out of the message — it carries a
                // signature.
                if (!uploadResponse.ok) {
                    throw new Error(`Image upload to storage failed: ${uploadResponse.status}`);
                }

                showNotification({
                    title: t("success"),
                    message: t("success_uploading_image"),
                    color: "green",
                    icon: null,
                });

                return response.uploadImage;
            } catch (err) {
                // client-api's uploadImage Lambda refuses an extension it does not know; a picker that
                // let one through (or a renamed file) lands here, and the formats are what the user needs.
                const isUnsupportedFormat =
                    err instanceof GraphQLRequestError &&
                    err.errorsNormalized.some(
                        (error) => error.errorType === "Lambda:Unhandled" && error.message === "Invalid image type"
                    );

                let message: Translation = { key: "error_uploading_image", namespace: "common" };
                if (isUnsupportedFormat) {
                    message = {
                        key: "image_unsupportedformat_message",
                        namespace: "common",
                        params: { filename: image.name, formats: IMAGE_FORMATS_LABEL },
                    };
                }

                handleError(err, { message });
            } finally {
                setIsUploadingImage(false);
            }
        },
        [t]
    );

    return {
        isUploadingImage,
        uploadImage,
    };
};
